'use client';

import BannerSection from '@/components/common/bottom-banner/bottom-banner-section';
import ProjectTimeline from '@/components/common/project-timeline/ProjectTimeline';
import CaseStudyMeta from "@/components/common/seo/case-study-meta";
import { caseStudies } from "@/lib/dto/case-studies/case-studies";
import * as Images from '@/lib/images';
import {
    DailyStatusUpdates,
    ImgCustomerSuccessStrategySuccessStatsCardDrRoss,
    Lower6AddRecord,
    Lower6CreateHealthProfile,
    Lower6Dashboard,
    Lower6RecordCalendar,
    Lower6ScanIntake,
    Lower6ScanResults,
    TrelloCard
} from '@/lib/images';
import Image from 'next/image';
import Link from 'next/link';

// Reusable Timeline Card Component
const TimelineCard = ({
    color,
    title,
    description,
    position,
    delay
}: {
    color: string;
    title: string;
    description: string;
    position: string;
    delay: number;
}) => {
    return (
        <div
            className={`absolute ${position} w-[200px] z-10 group`}
        >
            <div
                className={`${color} text-white px-6 py-4 rounded-xl shadow-md relative overflow-hidden`}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                <div className="flex items-center mb-2 relative z-10">
                    <span className="font-semibold">
                        {title}
                    </span>
                    <div
                        className="w-2 h-2 bg-white rounded-full ml-2"
                    ></div>
                </div>
            </div>
            <div
                className="bg-white p-4 rounded-xl shadow-md mt-1 relative overflow-hidden"
            >
                <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <p className="text-sm text-gray-700 relative z-10">{description}</p>
            </div>
        </div>
    );
};

// Phone Screenshot Component
const PhoneScreenshot = ({ src, alt, delay }: { src: string, alt: string, delay: number }) => {
    return (
        <div
            className="relative will-change-transform"
        >
            <div className="relative w-[180px] sm:w-[220px] lg:w-[260px] rounded-[30px]">
                <div className="rounded-lg shadow-lg overflow-hidden">
                    <Image
                        src={src}
                        alt={alt}
                        width={300}
                        height={600}
                        className="w-full h-auto"
                    />
                </div>
                <div className="absolute -bottom-4 left-0 right-0 h-4 bg-black/10 blur-md rounded-full mx-4"></div>
            </div>
        </div>
    );
};

// Lower6 app case study page
export default function Lower6AppCaseStudy() {
    // Find the case study data for this page
    const caseStudy = caseStudies.find(cs => cs.link === "/case-studies/vision-ai-ml-recognition-mobile-app");

    // Floating elements
    const FloatingElement = ({
        delay = 0,
        size = 20,
        left,
        top,
        opacity = 0.4,
        type = "circle"
    }: {
        delay?: number;
        size?: number;
        left: number;
        top: number;
        opacity?: number;
        type?: "circle" | "star" | "bubble";
    }) => {
        const getShape = () => {
            switch (type) {
                case "star":
                    return { clipPath: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)" };
                case "bubble":
                    return { borderRadius: "50% 50% 50% 70%/50% 50% 70% 60%", transform: "rotate(10deg)" };
                default:
                    return { borderRadius: "50%" };
            }
        };
        return (
            <div
                className="absolute bg-gradient-to-r from-green-400 to-emerald-500"
                style={{
                    width: size,
                    height: size,
                    left: `${left}%`,
                    top: `${top}%`,
                    opacity,
                    ...getShape()
                }}
            />
        );
    };

    return (
        <>
            {caseStudy && <CaseStudyMeta caseStudy={caseStudy} />}
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#F8F9FB' }}>
                    {/* Floating Elements */}
                    <FloatingElement left={10} top={20} delay={0} type="bubble" />
                    <FloatingElement left={85} top={15} delay={1} type="star" size={15} />
                    <FloatingElement left={75} top={70} delay={2} type="circle" size={25} />
                    <FloatingElement left={15} top={80} delay={1.5} type="bubble" size={18} />
                    <div className="container mx-auto px-4 lg:px-8 py-20">
                        {/* Top-left badge */}
                        <div
                            className="absolute top-8 left-8"
                        >
                            <span className="inline-block px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full text-sm font-medium shadow-sm border" style={{ color: '#2D2D2D' }}>
                                Mobile App Case Study
                            </span>
                        </div>
                        {/* Center-aligned content */}
                        <div className="text-center space-y-12">
                            {/* Title Section */}
                            <div
                                className="space-y-6 pt-28"
                            >
                                <div className="space-y-4">
                                    <h1 className="text-5xl lg:text-7xl font-semibold leading-tight">
                                        <span style={{ color: '#2563EB' }}>CaloriePal</span>
                                        <span className="pt-4 block text-5xl lg:text-6xl font-medium" style={{ color: '#2D2D2D' }}>Vision AI Food Recognition</span>
                                        <span className="block text-5xl lg:text-6xl font-medium" style={{ color: '#2D2D2D' }}>& Health Tracking App</span>
                                    </h1>
                                </div>
                            </div>
                            {/* Hero Mockup Image */}
                            <div
                                className="relative flex items-center justify-center mt-16"
                            >
                                <div className="relative">
                                    <div
                                        className="relative z-10"
                                    >
                                        <Image
                                            src={Images.Lower6HeroMockup}
                                            alt="Lower 6 - Vision AI Food Recognition & Health Tracking App Hero Mockup"
                                            width={800}
                                            height={600}
                                            className="w-full max-w-2xl h-auto drop-shadow-2xl"
                                            priority
                                        />
                                    </div>
                                    {/* Background decorative elements */}
                                    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10" style={{ backgroundColor: '#2563EB' }}></div>
                                    <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-orange-200 rounded-full opacity-20"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Project Reference Section (below HERO) */}
                <section className="relative bg-[#F8F9FB] py-16 border-b border-gray-100">
                    <div className="container mx-auto px-4 lg:px-8">
                        <div className="flex flex-col md:flex-row items-center md:items-stretch gap-12">
                            {/* Description Column */}
                            <div className="flex flex-col justify-center md:w-1/2 w-full h-full">

                                <div className="mb-6">
                                    <span className="text-3xl md:text-4xl font-light text-[#2563EB] leading-tight">
                                        "<span className="font-bold" style={{ color: '#2563EB' }}>CaloriePal</span> is a React Native mobile app that delivers instant food recognition, <span className="font-bold" style={{ color: '#2563EB' }}>real-time glycemic analysis</span>, and personalized health insights with native performance."
                                    </span>
                                </div>
                            </div>
                            {/* About Column with Large Icon below */}
                            <div className="flex flex-col justify-between md:w-1/2 w-full h-full relative">
                                <div className="flex flex-col z-10">
                                    <h3 className="text-[#2563EB] font-bold text-xl mb-2">Overview</h3>
                                    <p className="text-gray-500 text-base mb-6 max-w-lg">
                                        CaloriePal is an AI mobile app development project — a cross-platform{' '}
                                        <Link
                                            href="/mobile-app-development-services"
                                            className="font-medium text-[#2563EB] underline decoration-[#2563EB]/40 underline-offset-2 hover:decoration-[#2563EB]"
                                        >
                                            React Native mobile app
                                        </Link>{' '}
                                        delivering AI-powered food recognition, real-time glycemic tracking, and intelligent health insights across iOS and Android devices.
                                    </p>
                                    <div className="flex flex-wrap gap-3 mb-8">
                                        <span className="bg-[#2563EB12] text-black px-5 py-2 rounded-full text-sm font-medium">React Native</span>
                                        <span className="bg-[#2563EB12] text-black px-5 py-2 rounded-full text-sm font-medium">Mobile App</span>
                                        <span className="bg-[#2563EB12] text-black px-5 py-2 rounded-full text-sm font-medium">UX Design</span>
                                        <span className="bg-[#2563EB12] text-black px-5 py-2 rounded-full text-sm font-medium">UI Design</span>
                                        <span className="bg-[#2563EB12] text-black px-5 py-2 rounded-full text-sm font-medium">Cross-Platform</span>
                                    </div>
                                </div>
                                {/* Large Icon with 20% opacity */}
                                <div className="flex justify-center items-center mt-8">
                                    <span className="text-[8rem] md:text-[10rem] lg:text-[12rem] select-none" style={{ opacity: 0.2, color: '#2563EB', lineHeight: 1 }} aria-hidden="true">🩺</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* App Features Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-4 lg:px-8">
                        {/* Headline/Description and App Features Chip Row */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-12 gap-4">
                            <div className="flex flex-col max-w-2xl">
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-black mb-4">
                                    Experience React Native performance with <span className="font-bold text-[#2563EB]">native mobile features.</span>
                                </h2>
                                <p className="text-gray-500 text-lg">
                                    Built with React Native, CaloriePal delivers native performance across iOS and Android with smooth animations, optimized rendering, and seamless device integration.
                                </p>
                            </div>
                            <div className="flex justify-start w-full md:w-auto">
                                <button className="border border-gray-300 rounded-full px-6 py-2 text-gray-700 bg-white hover:bg-gray-50 transition font-medium text-base">App Features</button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {/* Feature Card 1 */}
                            <div className="bg-white rounded-2xl p-8 flex flex-col items-center text-center relative overflow-hidden group transition-all duration-300 border border-gray-100"
                                style={{
                                    background: `
                                    linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.4) 100%),
                                    radial-gradient(circle at 0% 0%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    radial-gradient(circle at 100% 100%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    linear-gradient(to right, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px,
                                    linear-gradient(to bottom, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px
                                `
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <img src={Lower6Dashboard.src} alt="Dashboard" className="w-40 h-80 object-contain mb-6 z-10 group-hover:scale-105 transition-transform duration-500" />
                                <h3 className="text-xl font-semibold text-gray-900 z-10 group-hover:text-[#2563EB] transition-colors duration-300">Personal Dashboard</h3>
                            </div>
                            {/* Feature Card 2 */}
                            <div className="bg-white rounded-2xl p-8 flex flex-col items-center text-center relative overflow-hidden group transition-all duration-300 border border-gray-100"
                                style={{
                                    background: `
                                    linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.4) 100%),
                                    radial-gradient(circle at 0% 0%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    radial-gradient(circle at 100% 100%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    linear-gradient(to right, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px,
                                    linear-gradient(to bottom, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px
                                `
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <img src={Lower6CreateHealthProfile.src} alt="Create Health Profile" className="w-40 h-80 object-contain mb-6 z-10 group-hover:scale-105 transition-transform duration-500" />
                                <h3 className="text-xl font-semibold text-gray-900 z-10 group-hover:text-[#2563EB] transition-colors duration-300">Create Health Profile</h3>
                            </div>
                            {/* Feature Card 3 */}
                            <div className="bg-white rounded-2xl p-8 flex flex-col items-center text-center relative overflow-hidden group transition-all duration-300 border border-gray-100"
                                style={{
                                    background: `
                                    linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.4) 100%),
                                    radial-gradient(circle at 0% 0%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    radial-gradient(circle at 100% 100%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    linear-gradient(to right, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px,
                                    linear-gradient(to bottom, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px
                                `
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <img src={Lower6AddRecord.src} alt="Add Record" className="w-40 h-80 object-contain mb-6 z-10 group-hover:scale-105 transition-transform duration-500" />
                                <h3 className="text-xl font-semibold text-gray-900 z-10 group-hover:text-[#2563EB] transition-colors duration-300">Add Health Records</h3>
                            </div>
                            {/* Feature Card 4 */}
                            <div className="bg-white rounded-2xl p-8 flex flex-col items-center text-center relative overflow-hidden group transition-all duration-300 border border-gray-100"
                                style={{
                                    background: `
                                    linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.4) 100%),
                                    radial-gradient(circle at 0% 0%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    radial-gradient(circle at 100% 100%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    linear-gradient(to right, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px,
                                    linear-gradient(to bottom, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px
                                `
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <img src={Lower6ScanIntake.src} alt="Scan Intake" className="w-40 h-80 object-contain mb-6 z-10 group-hover:scale-105 transition-transform duration-500" />
                                <h3 className="text-xl font-semibold text-gray-900 z-10 group-hover:text-[#2563EB] transition-colors duration-300">Scan Food Intake</h3>
                            </div>
                            {/* Feature Card 5 */}
                            <div className="bg-white rounded-2xl p-8 flex flex-col items-center text-center relative overflow-hidden group transition-all duration-300 border border-gray-100"
                                style={{
                                    background: `
                                    linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.4) 100%),
                                    radial-gradient(circle at 0% 0%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    radial-gradient(circle at 100% 100%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    linear-gradient(to right, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px,
                                    linear-gradient(to bottom, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px
                                `
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <img src={Lower6ScanResults.src} alt="Scan Results" className="w-40 h-80 object-contain mb-6 z-10 group-hover:scale-105 transition-transform duration-500" />
                                <h3 className="text-xl font-semibold text-gray-900 z-10 group-hover:text-[#2563EB] transition-colors duration-300">Instant Results</h3>
                            </div>
                            {/* Feature Card 6 */}
                            <div className="bg-white rounded-2xl p-8 flex flex-col items-center text-center relative overflow-hidden group transition-all duration-300 border border-gray-100"
                                style={{
                                    background: `
                                    linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.4) 100%),
                                    radial-gradient(circle at 0% 0%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    radial-gradient(circle at 100% 100%, rgba(74, 210, 139, 0.05) 0%, transparent 50%),
                                    linear-gradient(to right, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px,
                                    linear-gradient(to bottom, rgba(243, 244, 246, 0.1) 1px, transparent 1px) 0 0 / 20px 20px
                                `
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <img src={Lower6RecordCalendar.src} alt="Record Calendar" className="w-40 h-80 object-contain mb-6 z-10 group-hover:scale-105 transition-transform duration-500" />
                                <h3 className="text-xl font-semibold text-gray-900 z-10 group-hover:text-[#2563EB] transition-colors duration-300">Record Calendar</h3>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Research Insights Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-4 lg:px-8">
                        {/* Headline/Description and User Profile Chip Row */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-12 gap-4">
                            <div className="flex flex-col max-w-2xl">
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-black mb-4">
                                    Research insights fuel a superior <span className="font-bold text-[#2563EB]">fitness experience.</span>
                                </h2>
                                <p className="text-gray-500 text-lg">
                                    User research provided valuable insights into needs, habits, and goals, enabling us to design a more tailored, effective, and enjoyable fitness journey that drives better results.
                                </p>
                            </div>
                            <div className="flex justify-start w-full md:w-auto">
                                <button className="border border-gray-300 rounded-full px-6 py-2 text-gray-700 bg-white hover:bg-gray-50 transition font-medium text-base">User Profile</button>
                            </div>
                        </div>
                        {/* Grid Layout */}
                        <div className="grid md:grid-cols-2 gap-8">
                            {/* User Card & Personality */}
                            <div className="flex flex-col gap-6 md:gap-8">
                                {/* User Card */}
                                <div className="bg-gradient-to-br from-blue-100/60 to-blue-50/0 rounded-2xl p-0 overflow-hidden relative shadow-sm aspect-[3/4] md:aspect-square">
                                    <div className="relative w-full h-full">
                                        <img src="/images/case-studies/vision-ai-ml-recognition-mobile-app/fitness-enthu.webp" alt="Fitness Enthusiast" className="object-cover w-full h-full" />
                                    </div>
                                </div>
                                {/* Personality Card (now below User Card) */}
                                <div className="bg-blue-50 rounded-2xl p-4 md:p-8 shadow-sm aspect-auto md:aspect-square">
                                    <h3 className="text-2xl md:text-4xl font-bold text-black mb-4 md:mb-8">Personality</h3>
                                    {/* Goal Commitment */}
                                    <div className="mb-4 md:mb-8">
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-1 sm:gap-0">
                                            <span className="text-gray-500 text-sm md:text-2xl font-normal">Goal Commitment</span>
                                            <span className="text-black text-lg md:text-2xl font-semibold">92%</span>
                                        </div>
                                        <div className="text-gray-500 text-sm md:text-base mb-2">Demonstrates strong dedication to achieving health milestones, consistently following through on set goals.</div>
                                        <div className="relative w-full h-8 md:h-12 flex items-center">
                                            {/* Bar background */}
                                            <div className="absolute left-0 right-0 mx-auto w-full h-4 md:h-6 rounded-full bg-blue-200 overflow-hidden"></div>
                                            {/* Filled Bar */}
                                            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-4 md:h-6 rounded-full bg-[#2563eb]" style={{ width: '92%' }}></div>
                                            {/* Stripes for unfilled part */}
                                            <div className="absolute rounded-full top-1/2 -translate-y-1/2 left-[92%] h-4 md:h-6" style={{ width: '8%', background: 'repeating-linear-gradient(135deg, #e0e7ff 0 8px, #c7d2fe 8px 16px)' }}></div>
                                            {/* Thumb/Knob */}
                                            <div className="absolute top-1/2 left-[92%] -translate-x-1/2 -translate-y-1/2 w-6 md:w-10 h-6 md:h-10 rounded-full border-2 md:border-4 border-white shadow-lg" style={{ background: 'radial-gradient(circle at 40% 40%, #93c5fd 60%, #2563eb 100%)', boxShadow: '0 0 0 2px #60a5fa55' }}></div>
                                        </div>
                                    </div>
                                    {/* Social Engagement Preference */}
                                    <div className="mb-4 md:mb-8">
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-1 sm:gap-0">
                                            <span className="text-gray-500 text-sm md:text-2xl font-normal">Social Engagement Preference</span>
                                            <span className="text-black text-lg md:text-2xl font-semibold">68%</span>
                                        </div>
                                        <div className="text-gray-500 text-sm md:text-base mb-2">Enjoys sharing progress and connecting with a supportive community for motivation and accountability.</div>
                                        <div className="relative w-full h-8 md:h-12 flex items-center">
                                            {/* Bar background */}
                                            <div className="absolute left-0 right-0 mx-auto w-full h-4 md:h-6 rounded-full bg-blue-200 overflow-hidden"></div>
                                            {/* Filled Bar */}
                                            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-4 md:h-6 rounded-full bg-[#2563eb]" style={{ width: '68%' }}></div>
                                            {/* Stripes for unfilled part */}
                                            <div className="absolute rounded-full top-1/2 -translate-y-1/2 left-[68%] h-4 md:h-6" style={{ width: '32%', background: 'repeating-linear-gradient(135deg, #e0e7ff 0 8px, #c7d2fe 8px 16px)' }}></div>
                                            {/* Thumb/Knob */}
                                            <div className="absolute top-1/2 left-[68%] -translate-x-1/2 -translate-y-1/2 w-6 md:w-10 h-6 md:h-10 rounded-full border-2 md:border-4 border-white shadow-lg" style={{ background: 'radial-gradient(circle at 40% 40%, #93c5fd 60%, #2563eb 100%)', boxShadow: '0 0 0 2px #60a5fa55' }}></div>
                                        </div>
                                    </div>
                                    {/* Routine Adaptability */}
                                    <div>
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-1 sm:gap-0">
                                            <span className="text-gray-500 text-sm md:text-2xl font-normal">Routine Adaptability</span>
                                            <span className="text-black text-lg md:text-2xl font-semibold">85%</span>
                                        </div>
                                        <div className="text-gray-500 text-sm md:text-base mb-2">Quickly adapts to new routines and embraces changes for continuous improvement in health habits.</div>
                                        <div className="relative w-full h-8 md:h-12 flex items-center">
                                            {/* Bar background */}
                                            <div className="absolute left-0 right-0 mx-auto w-full h-4 md:h-6 rounded-full bg-blue-200 overflow-hidden"></div>
                                            {/* Filled Bar */}
                                            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-4 md:h-6 rounded-full bg-[#2563eb]" style={{ width: '85%' }}></div>
                                            {/* Stripes for unfilled part */}
                                            <div className="absolute rounded-full top-1/2 -translate-y-1/2 left-[85%] h-4 md:h-6" style={{ width: '15%', background: 'repeating-linear-gradient(135deg, #e0e7ff 0 8px, #c7d2fe 8px 16px)' }}></div>
                                            {/* Thumb/Knob */}
                                            <div className="absolute top-1/2 left-[85%] -translate-x-1/2 -translate-y-1/2 w-6 md:w-10 h-6 md:h-10 rounded-full border-2 md:border-4 border-white shadow-lg" style={{ background: 'radial-gradient(circle at 40% 40%, #93c5fd 60%, #2563eb 100%)', boxShadow: '0 0 0 2px #60a5fa55' }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Quote & Goals */}
                            <div className="flex flex-col gap-6 md:gap-8">
                                {/* Quote Card (now above Goals) */}
                                <div className="bg-blue-50 rounded-2xl p-4 md:p-8 flex flex-col justify-center shadow-sm aspect-auto md:aspect-square">
                                    <span className="text-[#2563EB] text-4xl md:text-9xl leading-none mb-1 md:mb-2">"</span>
                                    <p className="text-lg md:text-5xl text-gray-500 opacity-40 font-normal mb-1 md:mb-2 leading-tight md:leading-normal">
                                        CaloriePal empowers me to make smarter choices every day. The personalized insights and effortless tracking keep me motivated and on top of my health goals.
                                    </p>
                                    <span className="text-[#2563EB] text-4xl md:text-9xl leading-none self-end">"</span>
                                </div>
                                {/* Goals Card */}
                                <div className="bg-white rounded-2xl p-4 md:p-8 shadow-sm border border-blue-100 aspect-auto md:aspect-square flex flex-col justify-between">
                                    <div>
                                        <ul className="mb-4 md:mb-8 space-y-4 md:space-y-8">
                                            <li className="flex items-center gap-3 md:gap-5">
                                                <span className="flex-shrink-0 w-8 md:w-10 h-8 md:h-10 rounded-full bg-[#2563EB] flex items-center justify-center mt-1">
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-6 md:h-6">
                                                        <path d="M7 13.5L11 17L17 9.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </span>
                                                <span className="text-base md:text-2xl text-gray-800 font-normal leading-snug">Build lasting healthy habits with intuitive daily tracking.</span>
                                            </li>
                                            <li className="flex items-center gap-3 md:gap-5">
                                                <span className="flex-shrink-0 w-8 md:w-10 h-8 md:h-10 rounded-full bg-[#2563EB] flex items-center justify-center mt-1">
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-6 md:h-6">
                                                        <path d="M7 13.5L11 17L17 9.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </span>
                                                <span className="text-base md:text-2xl text-gray-800 font-normal leading-snug">Gain actionable insights to optimize nutrition and wellness.</span>
                                            </li>
                                            <li className="flex items-center gap-3 md:gap-5">
                                                <span className="flex-shrink-0 w-8 md:w-10 h-8 md:h-10 rounded-full bg-[#2563EB] flex items-center justify-center mt-1">
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="md:w-6 md:h-6">
                                                        <path d="M7 13.5L11 17L17 9.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </span>
                                                <span className="text-base md:text-2xl text-gray-800 font-normal leading-snug">Achieve sustainable progress with personalized recommendations.</span>
                                            </li>
                                        </ul>
                                        <p className="text-sm md:text-lg text-gray-500 font-normal opacity-80 mt-4">
                                            CaloriePal is designed to seamlessly fit your lifestyle, making health management simple, engaging, and effective.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-white pb-8 pt-0" aria-label="Consultation call to action">
                    <div className="container mx-auto px-4 lg:px-8">
                        <div className="mx-auto max-w-3xl rounded-xl border border-gray-200 bg-gray-50 px-6 py-10 text-center md:px-10 md:py-12">
                            <h3 className="mb-3 text-xl font-semibold text-gray-900 md:text-2xl">
                                Building an AI-powered health app?
                            </h3>
                            <p className="mx-auto mb-6 max-w-xl text-gray-600">
                                We&apos;ve done it before — let&apos;s scope your project together.
                            </p>
                            <Link
                                href="/contact-us"
                                className="inline-flex items-center justify-center rounded-full bg-[#2563EB] px-6 py-3 text-base font-medium text-white shadow-sm transition hover:bg-[#1d4ed8]"
                            >
                                Book a free consultation →
                            </Link>
                        </div>
                    </div>
                </section>

                {/* App Interface Showcase Section */}
                <section className="py-20 bg-green-50">
                    <div className="container mx-auto px-4 lg:px-8">
                        {/* Three Screenshots in Line */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="flex justify-center">
                                <img
                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/dashboard-dark.webp"
                                    alt="Lower6 Dashboard Dark Mode"
                                    className="w-auto h-[600px] object-contain"
                                />
                            </div>

                            <div className="flex justify-center">
                                <img
                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/splash.webp"
                                    alt="Lower6 Splash Screen"
                                    className="w-auto h-[600px] object-contain"
                                />
                            </div>

                            <div className="flex justify-center">
                                <img
                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/settings.webp"
                                    alt="Lower6 Settings Screen"
                                    className="w-auto h-[600px] object-contain"
                                />
                            </div>
                        </div>
                    </div>
                </section>



                {/* Design Process */}
                <section className="py-16 md:py-20 bg-white">
                    <div className="container mx-auto px-4 lg:px-8">
                        {/* Headline/Description and Design Process Chip Row */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-8 md:mb-12 gap-4">
                            <div className="flex flex-col max-w-2xl">
                                <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light text-black mb-4">
                                    From concept to deployment, we leveraged <span className="font-bold text-[#2563EB]">React Native expertise.</span>
                                </h2>
                                <p className="text-gray-500 text-base md:text-lg">
                                    Our development process combined React Native's cross-platform capabilities with AI integration, creating a high-performance mobile app that feels native on both iOS and Android.
                                </p>
                            </div>
                            <div className="flex justify-start w-full md:w-auto">
                                <button className="border border-gray-300 rounded-full px-4 md:px-6 py-2 text-gray-700 bg-white hover:bg-gray-50 transition font-medium text-sm md:text-base">Project Timeline</button>
                            </div>
                        </div>

                        {/* Desktop/Laptop Project Timeline */}
                        <div className="hidden md:flex flex-col items-center">
                            <ProjectTimeline theme="light" />
                        </div>

                        {/* Mobile Project Timeline */}
                        <div className="block md:hidden">
                            <div className="relative">
                                {/* Connecting Line */}
                                <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200"></div>

                                <div className="space-y-8">
                                    {/* Timeline Item 1 */}
                                    <div className="flex items-start space-x-4 relative">
                                        <div className="flex-shrink-0 relative z-10">
                                            <div className="w-8 h-8 bg-[#2563EB] rounded-full flex items-center justify-center shadow-sm">
                                                <span className="text-white text-sm font-bold">1</span>
                                            </div>
                                        </div>
                                        <div className="flex-1 pt-0.5">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Research & Planning</h3>
                                            <p className="text-sm text-gray-600 mb-3">Understanding user needs and defining project scope</p>
                                            <div className="text-xs text-[#2563EB] font-medium bg-[#2563EB]/10 px-2 py-1 rounded-full inline-block">Week 1-2</div>
                                        </div>
                                    </div>

                                    {/* Timeline Item 2 */}
                                    <div className="flex items-start space-x-4 relative">
                                        <div className="flex-shrink-0 relative z-10">
                                            <div className="w-8 h-8 bg-[#2563EB] rounded-full flex items-center justify-center shadow-sm">
                                                <span className="text-white text-sm font-bold">2</span>
                                            </div>
                                        </div>
                                        <div className="flex-1 pt-0.5">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">UI/UX Design</h3>
                                            <p className="text-sm text-gray-600 mb-3">Creating wireframes and visual designs</p>
                                            <div className="text-xs text-[#2563EB] font-medium bg-[#2563EB]/10 px-2 py-1 rounded-full inline-block">Week 3-4</div>
                                        </div>
                                    </div>

                                    {/* Timeline Item 3 */}
                                    <div className="flex items-start space-x-4 relative">
                                        <div className="flex-shrink-0 relative z-10">
                                            <div className="w-8 h-8 bg-[#2563EB] rounded-full flex items-center justify-center shadow-sm">
                                                <span className="text-white text-sm font-bold">3</span>
                                            </div>
                                        </div>
                                        <div className="flex-1 pt-0.5">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Development</h3>
                                            <p className="text-sm text-gray-600 mb-3">React Native app development and testing</p>
                                            <div className="text-xs text-[#2563EB] font-medium bg-[#2563EB]/10 px-2 py-1 rounded-full inline-block">Week 5-8</div>
                                        </div>
                                    </div>

                                    {/* Timeline Item 4 */}
                                    <div className="flex items-start space-x-4 relative">
                                        <div className="flex-shrink-0 relative z-10">
                                            <div className="w-8 h-8 bg-[#2563EB] rounded-full flex items-center justify-center shadow-sm">
                                                <span className="text-white text-sm font-bold">4</span>
                                            </div>
                                        </div>
                                        <div className="flex-1 pt-0.5">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Launch & Deploy</h3>
                                            <p className="text-sm text-gray-600 mb-3">App store submission and final deployment</p>
                                            <div className="text-xs text-[#2563EB] font-medium bg-[#2563EB]/10 px-2 py-1 rounded-full inline-block">Week 9-10</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-10 text-center md:text-left">
                            <Link
                                href="/case-studies"
                                className="text-base font-medium text-[#2563EB] hover:underline"
                            >
                                ← See more case studies
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Development Process Steps Section */}
                <section className="py-16 md:py-20 bg-blue-500 text-white relative overflow-hidden">
                    {/* Background Image Placeholder */}
                    <div className="absolute inset-0 bg-[#2563EB]"></div>

                    <div className="container mx-auto px-4 lg:px-8 relative z-10">
                        {/* Headline/Description and Creation Process Chip Row */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-8 md:mb-12 gap-4">
                            <div className="flex flex-col max-w-2xl">
                                <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light text-white mb-4">
                                    Devkrest approach for <span className="font-bold text-white">exceptional results.</span>
                                </h2>
                                <p className="text-white/80 text-base md:text-lg">
                                    From initial consultation to app store deployment, our React Native development process combines strategic planning, mobile-first design, and cross-platform technical excellence to deliver outstanding native mobile experiences.
                                </p>
                            </div>
                            <div className="flex justify-start w-full md:w-auto">
                                <button className="border border-white/30 rounded-full px-4 md:px-6 py-2 text-white bg-white/10 hover:bg-white/20 transition font-medium text-sm md:text-base backdrop-blur-sm">Creation Process</button>
                            </div>
                        </div>

                        {/* Desktop/Laptop Process Cards Grid */}
                        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* 01 Consultation & Research */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white hover:text-gray-900 transition-all duration-300 group">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                                        <span className="text-white text-sm font-bold">01</span>
                                    </div>
                                    <h3 className="text-xl font-semibold text-white group-hover:text-gray-900">Consultation & Research</h3>
                                </div>
                                <ul className="space-y-2 text-white/90 group-hover:text-gray-700 text-sm">
                                    <li>• Client requirements analysis</li>
                                    <li>• Market research & trends</li>
                                    <li>• User persona development</li>
                                    <li>• Competitive analysis</li>
                                    <li>• Technical feasibility study</li>
                                </ul>
                            </div>

                            {/* 02 UI Design & Prototyping */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white hover:text-gray-900 transition-all duration-300 group">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                                        <span className="text-white text-sm font-bold">02</span>
                                    </div>
                                    <h3 className="text-xl font-semibold text-white group-hover:text-gray-900">UI Design & Prototyping</h3>
                                </div>
                                <ul className="space-y-2 text-white/90 group-hover:text-gray-700 text-sm">
                                    <li>• Wireframe creation</li>
                                    <li>• Visual design concepts</li>
                                    <li>• Interactive prototypes</li>
                                    <li>• Design system development</li>
                                    <li>• User testing & iteration</li>
                                </ul>
                            </div>

                            {/* 03 Development */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white hover:text-gray-900 transition-all duration-300 group">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                                        <span className="text-white text-sm font-bold">03</span>
                                    </div>
                                    <h3 className="text-xl font-semibold text-white group-hover:text-gray-900">Development</h3>
                                </div>
                                <ul className="space-y-2 text-white/90 group-hover:text-gray-700 text-sm">
                                    <li>• React Native app development</li>
                                    <li>• Native module integration</li>
                                    <li>
                                        •{' '}
                                        <Link
                                            href="/mobile-app-development-services"
                                            className="font-medium text-white/90 underline decoration-white/50 underline-offset-2 group-hover:text-gray-700 group-hover:decoration-gray-700/50"
                                        >
                                            cross-platform optimization
                                        </Link>
                                    </li>
                                    <li>• API & backend services</li>
                                    <li>• iOS & Android testing</li>
                                </ul>
                            </div>

                            {/* 04 Launch */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white hover:text-gray-900 transition-all duration-300 group">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                                        <span className="text-white text-sm font-bold">04</span>
                                    </div>
                                    <h3 className="text-xl font-semibold text-white group-hover:text-gray-900">Launch</h3>
                                </div>
                                <ul className="space-y-2 text-white/90 group-hover:text-gray-700 text-sm">
                                    <li>• App Store deployment</li>
                                    <li>• Performance monitoring</li>
                                    <li>• User onboarding & support</li>
                                    <li>• Mobile analytics implementation</li>
                                    <li>• Ongoing app updates</li>
                                </ul>
                            </div>
                        </div>

                        {/* Mobile Process Steps - Stacked Layout */}
                        <div className="block md:hidden space-y-4">
                            {/* 01 Consultation & Research */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center">
                                        <span className="text-white text-xs font-bold">01</span>
                                    </div>
                                    <h3 className="text-base font-semibold text-white">Consultation & Research</h3>
                                </div>
                                <p className="text-white/80 text-sm">Client requirements analysis, market research, user personas, and competitive analysis.</p>
                            </div>

                            {/* 02 UI Design & Prototyping */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center">
                                        <span className="text-white text-xs font-bold">02</span>
                                    </div>
                                    <h3 className="text-base font-semibold text-white">UI Design & Prototyping</h3>
                                </div>
                                <p className="text-white/80 text-sm">Wireframes, visual designs, interactive prototypes, and design system development.</p>
                            </div>

                            {/* 03 Development */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center">
                                        <span className="text-white text-xs font-bold">03</span>
                                    </div>
                                    <h3 className="text-base font-semibold text-white">Development</h3>
                                </div>
                                <p className="text-white/80 text-sm">
                                    React Native development, native modules,{' '}
                                    <Link
                                        href="/mobile-app-development-services"
                                        className="font-medium text-white underline decoration-white/50 underline-offset-2 hover:decoration-white"
                                    >
                                        cross-platform optimization
                                    </Link>
                                    , and testing.
                                </p>
                            </div>

                            {/* 04 Launch */}
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center">
                                        <span className="text-white text-xs font-bold">04</span>
                                    </div>
                                    <h3 className="text-base font-semibold text-white">Launch</h3>
                                </div>
                                <p className="text-white/80 text-sm">App store deployment, performance monitoring, user support, and analytics implementation.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Theming and Typography Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-4 lg:px-8">
                        {/* Headline/Description and Typography Chip Row */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-12 gap-4">
                            <div className="flex flex-col max-w-2xl">
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-black mb-4">
                                    Consistent design system with <span className="font-bold text-[#2563EB]">typography and colors.</span>
                                </h2>
                                <p className="text-gray-500 text-lg">
                                    A well-defined design system ensures consistency across the mobile app, featuring carefully selected typography and a cohesive color palette that enhances user experience and brand identity.
                                </p>
                            </div>
                            <div className="flex justify-start w-full md:w-auto">
                                <button className="border border-gray-300 rounded-full px-6 py-2 text-gray-700 bg-white hover:bg-gray-50 transition font-medium text-base">Design System</button>
                            </div>
                        </div>

                        {/* Typography and Color Layout - Column */}
                        <div className="space-y-16">
                            {/* Typography Section */}
                            <div className="py-8 lg:py-12">
                                <div className="mb-8">
                                    <h3 className="text-xl font-semibold text-gray-600 mb-2 tracking-wide">TYPOGRAPHY</h3>
                                    <p className="text-sm text-gray-500 font-medium">Light / Regular / Medium / Bold</p>
                                </div>

                                {/* Main Font Display */}
                                <div className="relative mb-8">
                                    <div className="border-2 border-dashed border-blue-300 rounded-2xl p-8 bg-white/50 relative">
                                        <h1 className="text-6xl lg:text-7xl font-bold text-black mb-4" style={{ fontFamily: 'Plus Jakarta Sans, system-ui, sans-serif' }}>
                                            Plus Jakarta Sans <span className="text-black">Font</span>
                                        </h1>
                                        {/* Enhanced Figma Cursor Arrow with Tooltip */}
                                        <div className="absolute top-12 right-16 pointer-events-none group">
                                            {/* Tooltip */}

                                            {/* Cursor Image */}
                                            <Image
                                                src="/images/case-studies/vision-ai-ml-recognition-mobile-app/cursor.webp"
                                                alt="Devkrest Team Cursor"
                                                width={200}
                                                height={200}
                                                className="drop-shadow-xl"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Font Description */}
                                <div className="grid md:grid-cols-2 gap-8 mb-8">
                                    <div>
                                        <p className="text-gray-600 text-lg leading-relaxed">
                                            Plus Jakarta Sans is a modern sans-serif font family designed for versatility and clarity. It offers excellent readability across digital interfaces and is known for its clean, friendly, and professional appearance.
                                        </p>
                                    </div>
                                    <div className="space-y-3">
                                        <p className="text-gray-800 font-semibold text-xl">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
                                        <p className="text-gray-600 text-xl">abcdefghijklmnopqrstuvwxyz</p>
                                        <p className="text-gray-600 text-xl">0123456789!"#$%&'()@=?+-</p>
                                    </div>
                                </div>
                            </div>

                            {/* Color Palette Section */}
                            <div className="space-y-6">
                                {/* Primary Colors Row */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {/* Dark Green */}
                                    <div className="bg-[#0D308C] rounded-3xl p-8 text-white relative overflow-hidden">
                                        <div className="absolute top-4 right-4">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white/70">
                                                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <h3 className="text-2xl font-semibold mb-4">Dark Blue</h3>
                                        <div className="mt-auto">
                                            <p className="text-white/80 text-sm mb-1">Hex Code :</p>
                                            <p className="text-white font-mono text-lg">#0D308C</p>
                                        </div>
                                    </div>

                                    {/* Light Green */}
                                    <div className="bg-[#50ABFF] rounded-3xl p-8 text-black relative overflow-hidden">
                                        <div className="absolute top-4 right-4">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-black/70">
                                                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <h3 className="text-2xl font-semibold mb-4">Light Blue</h3>
                                        <div className="mt-auto">
                                            <p className="text-black/80 text-sm mb-1">Hex Code :</p>
                                            <p className="text-black font-mono text-lg">#50ABFF</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Secondary Colors Row */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {/* Black */}
                                    <div className="bg-gradient-to-br from-[#1F1F1F] to-[#0A0A0A] rounded-3xl p-8 text-white relative overflow-hidden">
                                        <div className="absolute top-4 right-4">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white/70">
                                                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <h3 className="text-2xl font-semibold mb-4">Black</h3>
                                        <div className="mt-auto">
                                            <p className="text-white/80 text-sm mb-1">Hex Code :</p>
                                            <p className="text-white font-mono text-lg">#1F1F1F</p>
                                        </div>
                                    </div>

                                    {/* Grey */}
                                    <div className="bg-gradient-to-br from-[#6B7280] to-[#4B5563] rounded-3xl p-8 text-white relative overflow-hidden">
                                        <div className="absolute top-4 right-4">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-white/70">
                                                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <h3 className="text-2xl font-semibold mb-4">Grey</h3>
                                        <div className="mt-auto">
                                            <p className="text-white/80 text-sm mb-1">Hex Code :</p>
                                            <p className="text-white font-mono text-lg">#6B7280</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* User Flow Section */}
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4 lg:px-8">
                        {/* Section Header */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-16 gap-4">
                            <div className="flex flex-col max-w-2xl">
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-black mb-4">
                                    User flow guides seamless navigation <span className="font-bold text-[#2563EB]">through the app.</span>
                                </h2>
                                <p className="text-gray-500 text-lg">
                                    The user flow guides seamless navigation through the app, ensuring easy access to features, clear actions, and a smooth overall experience.
                                </p>
                            </div>
                            <div className="flex justify-start w-full md:w-auto">
                                <button className="border border-gray-300 rounded-full px-6 py-2 text-gray-700 bg-white hover:bg-gray-50 transition font-medium text-base">User Flow</button>
                            </div>
                        </div>

                        {/* User Flow Flowchart */}
                        <div className="flex justify-center overflow-hidden">
                            <Image
                                src="/images/case-studies/vision-ai-ml-recognition-mobile-app/flowchart.webp"
                                alt="Lower6 App User Flow Diagram"
                                width={1200}
                                height={800}
                                className="w-full md:scale-125 max-w-5xl h-auto mb-20"
                            />
                        </div>
                    </div>
                </section>

                {/* Wireframes Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-4 lg:px-8">
                        {/* Section Header */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-16 gap-4">
                            <div className="flex flex-col max-w-2xl">
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-black mb-4">
                                    From concept to completion with <span className="font-bold text-[#2563EB]">detailed wireframes.</span>
                                </h2>
                                <p className="text-gray-500 text-lg">
                                    Our comprehensive wireframing process ensures every user interaction is carefully planned and optimized for the best possible experience across the caloriepal mobile app.
                                </p>
                            </div>
                            <div className="flex justify-start w-full md:w-auto">
                                <button className="border border-gray-300 rounded-full px-6 py-2 text-gray-700 bg-white hover:bg-gray-50 transition font-medium text-base">Wireframes</button>
                            </div>
                        </div>

                        {/* Authentication & Onboarding Section */}
                        <div className="mb-20">
                            <div className="mb-8">
                                <h3 className="text-2xl font-semibold text-gray-900 mb-3">Authentication & Onboarding</h3>
                                <p className="text-gray-600 text-lg">First impressions matter - smooth onboarding and secure authentication flow.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-12">
                                <div className="aspect-square bg-gray-50 rounded-2xl p-12 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-6">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_1.webp"
                                            alt="Splash Screen Wireframe"
                                            width={400}
                                            height={800}
                                            className="w-auto h-full max-h-96 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-lg font-semibold text-gray-800 text-center">Splash Screen</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-12 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-6">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_2.webp"
                                            alt="Onboarding Screen Wireframe"
                                            width={400}
                                            height={800}
                                            className="w-auto h-full max-h-96 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-lg font-semibold text-gray-800 text-center">Onboarding</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-12 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-6">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_3.webp"
                                            alt="Login Screen Wireframe"
                                            width={400}
                                            height={800}
                                            className="w-auto h-full max-h-96 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-lg font-semibold text-gray-800 text-center">Login Screen</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-12 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-6">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_4.webp"
                                            alt="Signup Screen Wireframe"
                                            width={400}
                                            height={800}
                                            className="w-auto h-full max-h-96 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-lg font-semibold text-gray-800 text-center">Signup Screen</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-12 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-6">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_5.webp"
                                            alt="Password Recovery Wireframe"
                                            width={400}
                                            height={800}
                                            className="w-auto h-full max-h-96 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-lg font-semibold text-gray-800 text-center">Password Recovery</h4>
                                </div>
                            </div>
                        </div>

                        {/* Profile Management Section */}
                        <div className="mb-20">
                            <div className="mb-8">
                                <h3 className="text-2xl font-semibold text-gray-900 mb-3">Profile Management</h3>
                                <p className="text-gray-600 text-lg">Personalized health profiles and user account management.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                                <div className="aspect-square bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-4">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_6.webp"
                                            alt="Create Health Profile Wireframe"
                                            width={500}
                                            height={1000}
                                            className="w-auto h-full max-h-128 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 text-center">Create Profile</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-4">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_7.webp"
                                            alt="Edit Health Profile Wireframe"
                                            width={500}
                                            height={1000}
                                            className="w-auto h-full max-h-128 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 text-center">Edit Profile</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-4">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_8.webp"
                                            alt="Scan History Wireframe"
                                            width={500}
                                            height={1000}
                                            className="w-auto h-full max-h-128 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 text-center">Scan History</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-4">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_10.webp"
                                            alt="Dashboard View Wireframe"
                                            width={500}
                                            height={1000}
                                            className="w-auto h-full max-h-128 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 text-center">Dashboard View</h4>
                                </div>
                            </div>
                        </div>

                        {/* Analytics & Insights Section */}
                        <div className="mb-8">
                            <div className="mb-8">
                                <h3 className="text-2xl font-semibold text-gray-900 mb-3">Analytics & Insights</h3>
                                <p className="text-gray-600 text-lg">Comprehensive analytics, detailed insights, and advanced health tracking features.</p>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-18">
                                <div className="aspect-square bg-gray-50 rounded-2xl p-18 hover:shadow-2xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-8">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_11.webp"
                                            alt="Settings Wireframe"
                                            width={500}
                                            height={1000}
                                            className="w-auto h-full max-h-144 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 text-center">Settings</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-18 hover:shadow-2xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-8">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_12.webp"
                                            alt="Calendar Logs Wireframe"
                                            width={500}
                                            height={1000}
                                            className="w-auto h-full max-h-144 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 text-center">Calendar Logs</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-18 hover:shadow-2xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-8">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_13.webp"
                                            alt="Health Insights Wireframe"
                                            width={500}
                                            height={1000}
                                            className="w-auto h-full max-h-144 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 text-center">Health Insights</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-18 hover:shadow-2xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-8">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_14.webp"
                                            alt="Data Visualization Wireframe"
                                            width={500}
                                            height={1000}
                                            className="w-auto h-full max-h-144 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 text-center">Data Visualization</h4>
                                </div>
                                <div className="aspect-square bg-gray-50 rounded-2xl p-18 hover:shadow-2xl transition-shadow duration-300 flex flex-col items-center justify-center">
                                    <div className="w-full h-full flex items-center justify-center mb-8">
                                        <Image
                                            src="/images/case-studies/vision-ai-ml-recognition-mobile-app/wireframes/wireframe_15.webp"
                                            alt="Advanced Settings Wireframe"
                                            width={500}
                                            height={1000}
                                            className="w-auto h-full max-h-144 object-contain"
                                        />
                                    </div>
                                    <h4 className="text-xl font-semibold text-gray-800 text-center">Advanced Settings</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Tech Stack Section */}
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4 lg:px-8">
                        {/* Section Header */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-12 gap-4">
                            <div className="flex flex-col max-w-2xl">
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-black mb-4">
                                    Built with powerful technologies for <span className="font-bold text-[#2563EB]">exceptional performance.</span>
                                </h2>
                                <p className="text-gray-500 text-lg">
                                    Our carefully selected technology stack ensures scalability, performance, and maintainability while delivering the best user experience across all platforms.
                                </p>
                            </div>
                            <div className="flex justify-start w-full md:w-auto">
                                <button className="border border-gray-300 rounded-full px-6 py-2 text-gray-700 bg-white hover:bg-gray-50 transition font-medium text-base">Tech Stack</button>
                            </div>
                        </div>

                        {/* Row Layout */}
                        <div className="space-y-6">
                            {/* Row 1: Frameworks (Full Width) */}
                            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 bg-[#2563EB]/10 rounded-xl flex items-center justify-center">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#2563EB]">
                                            <path d="M10 3V4.4C10 4.96005 10 5.24008 10.109 5.45399C10.2049 5.64215 10.3578 5.79513 10.546 5.89101C10.7599 6 11.0399 6 11.6 6H12.4C12.9601 6 13.2401 6 13.454 5.89101C13.6422 5.79513 13.7951 5.64215 13.891 5.45399C14 5.24008 14 4.96005 14 4.4V3M9.2 21H14.8C15.9201 21 16.4802 21 16.908 20.782C17.2843 20.5903 17.5903 20.2843 17.782 19.908C18 19.4802 18 18.9201 18 17.8V6.2C18 5.0799 18 4.51984 17.782 4.09202C17.5903 3.71569 17.2843 3.40973 16.908 3.21799C16.4802 3 15.9201 3 14.8 3H9.2C8.0799 3 7.51984 3 7.09202 3.21799C6.71569 3.40973 6.40973 3.71569 6.21799 4.09202C6 4.51984 6 5.07989 6 6.2V17.8C6 18.9201 6 19.4802 6.21799 19.908C6.40973 20.2843 6.71569 20.5903 7.09202 20.782C7.51984 21 8.07989 21 9.2 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900">FRAMEWORKS</h3>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                        <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                            <Image
                                                src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/frameworks/react-native.svg"
                                                alt="React Native"
                                                width={24}
                                                height={24}
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        <span className="text-xs font-medium text-gray-700 text-center">REACT NATIVE</span>
                                    </div>
                                    <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                        <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                            <Image
                                                src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/frameworks/nextjs.svg"
                                                alt="Next.js"
                                                width={24}
                                                height={24}
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        <span className="text-xs font-medium text-gray-700 text-center">NEXT.JS</span>
                                    </div>
                                    <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                        <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                            <Image
                                                src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/frameworks/react.svg"
                                                alt="React"
                                                width={24}
                                                height={24}
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        <span className="text-xs font-medium text-gray-700 text-center">REACT</span>
                                    </div>
                                    <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                        <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                            <Image
                                                src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/frameworks/nodejs.svg"
                                                alt="Node.js"
                                                width={24}
                                                height={24}
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        <span className="text-xs font-medium text-gray-700 text-center">NODE.JS</span>
                                    </div>
                                </div>
                            </div>

                            {/* Row 2: Cloud Services & Testing Tools (Two Columns) */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Cloud Services & Hosting */}
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 bg-[#2563EB]/10 rounded-xl flex items-center justify-center">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#2563EB]">
                                                <path d="M11.0947 8.02658C11.5476 5.73111 13.5717 4 16 4C18.7614 4 21 6.23858 21 9C21 11.0345 19.7849 12.7852 18.0408 13.5659M11.0947 8.02658C9.24194 8.21766 7.68947 9.4193 7 11C4.6 11.375 3 13.3144 3 15.4137C3 17.9466 5.14903 20 7.8 20L15 20C17.2091 20 19 18.2719 19 16.1402C19 15.1829 18.6388 14.2698 18.0408 13.5659M11.0947 8.02658C11.265 8.00902 11.4378 8 11.6127 8C14.2747 8 16.4504 9.99072 16.6 12.5C17.1583 12.7354 17.6501 13.106 18.0408 13.5659" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900">CLOUD SERVICES & HOSTING</h3>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/cloud-hosting/aws.svg"
                                                    alt="AWS"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">AWS</span>
                                        </div>
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/cloud-hosting/digitalocean.svg"
                                                    alt="DigitalOcean"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">DIGITALOCEAN</span>
                                        </div>
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/cloud-hosting/docker.svg"
                                                    alt="Docker"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">DOCKER</span>
                                        </div>
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/cloud-hosting/vercel.svg"
                                                    alt="Vercel"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">VERCEL</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Testing Tools */}
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 bg-[#2563EB]/10 rounded-xl flex items-center justify-center">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#2563EB]">
                                                <path d="M9 12L11 14L15 10M21 12C21 16.97 16.97 21 12 21S3 16.97 3 12S7.03 3 12 3S21 7.03 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900">TESTING TOOLS</h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/testing/cypress.svg"
                                                    alt="Cypress"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">CYPRESS</span>
                                        </div>
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/testing/postman.svg"
                                                    alt="Postman"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">POSTMAN</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Row 3: Programming, Data Store, DevOps, AI & ML (Four Columns) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {/* Programming Languages */}
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 bg-[#2563EB]/10 rounded-xl flex items-center justify-center">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#2563EB]">
                                                <path d="M7 8L3 12L7 16M17 8L21 12L17 16M14 4L10 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900">PROGRAMMING</h3>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/programming/typescript.svg"
                                                    alt="TypeScript"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">TYPESCRIPT</span>
                                        </div>
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/programming/javascript.webp"
                                                    alt="JavaScript"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">JAVASCRIPT</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Data Store */}
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 bg-[#2563EB]/10 rounded-xl flex items-center justify-center">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#2563EB]">
                                                <ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" strokeWidth="2" />
                                                <path d="M21 12C21 13.66 16.97 15 12 15S3 13.66 3 12" stroke="currentColor" strokeWidth="2" />
                                                <path d="M3 5V19C3 20.66 7.03 22 12 22S21 20.66 21 19V5" stroke="currentColor" strokeWidth="2" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900">DATA STORE</h3>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/data-store/firebase.svg"
                                                    alt="Firebase"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">FIREBASE</span>
                                        </div>
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/data-store/mysql.svg"
                                                    alt="MySQL"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">MYSQL</span>
                                        </div>
                                    </div>
                                </div>

                                {/* DevOps & CI/CD Tools */}
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 bg-[#2563EB]/10 rounded-xl flex items-center justify-center">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#2563EB]">
                                                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                                <path d="M19.4 15A1.65 1.65 0 0 0 21 13.35V10.65A1.65 1.65 0 0 0 19.4 9L17.25 9.68A7.72 7.72 0 0 0 16 8.68L15.32 6.53A1.65 1.65 0 0 0 13.67 5H10.33A1.65 1.65 0 0 0 8.68 6.53L8 8.68A7.72 7.72 0 0 0 6.75 9.68L4.6 9A1.65 1.65 0 0 0 3 10.65V13.35A1.65 1.65 0 0 0 4.6 15L6.75 14.32A7.72 7.72 0 0 0 8 15.32L8.68 17.47A1.65 1.65 0 0 0 10.33 19H13.67A1.65 1.65 0 0 0 15.32 17.47L16 15.32A7.72 7.72 0 0 0 17.25 14.32L19.4 15Z" stroke="currentColor" strokeWidth="2" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900">DEVOPS</h3>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/devops/gitlab.svg"
                                                    alt="GitLab"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">GITLAB</span>
                                        </div>
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/devops/github.svg"
                                                    alt="GitHub"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">GITHUB</span>
                                        </div>
                                    </div>
                                </div>

                                {/* AI & Machine Learning */}
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 bg-[#2563EB]/10 rounded-xl flex items-center justify-center">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#2563EB]">
                                                <path d="M9.663 17H4.5A2.5 2.5 0 0 1 2 14.5V9.5A2.5 2.5 0 0 1 4.5 7H9.663" stroke="currentColor" strokeWidth="2" />
                                                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                                <path d="M14.337 7H19.5A2.5 2.5 0 0 1 22 9.5V14.5A2.5 2.5 0 0 1 19.5 17H14.337" stroke="currentColor" strokeWidth="2" />
                                            </svg>
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            <Link
                                                href="/gen-ai-development-services"
                                                className="text-gray-900 hover:text-[#2563EB]"
                                            >
                                                AI & ML
                                            </Link>
                                        </h3>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/ai-ml/openai.svg"
                                                    alt="OpenAI"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">OPENAI</span>
                                        </div>
                                        <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-2 p-2">
                                                <Image
                                                    src="/images/case-studies/vision-ai-ml-recognition-mobile-app/tech-stack/ai-ml/vision-ai.svg"
                                                    alt="TensorFlow"
                                                    width={24}
                                                    height={24}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 text-center">TENSORFLOW</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* App Screenshots Gallery Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-4 lg:px-8">
                        {/* Headline/Description and Screenshots Chip Row */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-12 gap-4">
                            <div className="flex flex-col max-w-2xl">
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-black mb-4">
                                    Comprehensive showcase of <span className="font-bold text-[#2563EB]">caloriepal app screens.</span>
                                </h2>
                                <p className="text-gray-500 text-lg">
                                    Explore CaloriePal&apos;s full UI — from food scanning to health dashboards across iOS and Android — with intuitive design, seamless navigation, and health tracking across every screen and user journey.
                                </p>
                            </div>
                            <div className="flex justify-start w-full md:w-auto">
                                <button className="border border-gray-300 rounded-full px-6 py-2 text-gray-700 bg-white hover:bg-gray-50 transition font-medium text-base">App Screenshots</button>
                            </div>
                        </div>

                        {/* Screenshots Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {/* Screenshot 1 */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/screenshots/screen-1.webp"
                                        alt="Lower6 App Screen 1"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Screenshot 2 */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/screenshots/screen-2.webp"
                                        alt="Lower6 App Screen 2"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Screenshot 3 */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/screenshots/screen-3.webp"
                                        alt="Lower6 App Screen 3"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Screenshot 4 */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/screenshots/screen-4.webp"
                                        alt="Lower6 App Screen 4"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Screenshot 5 */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/screenshots/screen-5.webp"
                                        alt="Lower6 App Screen 5"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Screenshot 6 */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/screenshots/screen-6.webp"
                                        alt="Lower6 App Screen 6"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Screenshot 7 */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/screenshots/screen-7.webp"
                                        alt="Lower6 App Screen 7"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Screenshot 8 */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/screenshots/screen-8.webp"
                                        alt="Lower6 App Screen 8"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Add Record Screenshot */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/add-record.webp"
                                        alt="Lower6 Add Record Screen"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Create Health Profile Screenshot */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/create-health-profile.webp"
                                        alt="Lower6 Create Health Profile Screen"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Dashboard Dark Screenshot */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/dashboard-dark.webp"
                                        alt="Lower6 Dashboard Dark Mode Screen"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Dashboard Screenshot */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/dashboard.webp"
                                        alt="Lower6 Dashboard Screen"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Record Calendar Screenshot */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/record-calendar.webp"
                                        alt="Lower6 Record Calendar Screen"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Scan Intake Screenshot */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/scan-intake.webp"
                                        alt="Lower6 Scan Intake Screen"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>

                            {/* Scan Results Screenshot */}
                            <div className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden group transition-all duration-300 border border-gray-100 hover:shadow-lg">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-transparent to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-100 via-green-50 to-blue-100 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
                                <div className="relative z-10 group-hover:scale-105 transition-transform duration-500">
                                    <img
                                        src="/images/case-studies/vision-ai-ml-recognition-mobile-app/scan-results.webp"
                                        alt="Lower6 Scan Results Screen"
                                        className="w-full h-auto max-h-80 object-contain"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
          
                {/* Communication: The Secret Sauce to Our Success */}
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4 lg:px-8">
                        {/* Section Header */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-16 gap-4">
                            <div className="flex flex-col max-w-2xl">
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-black mb-4">
                                    Communication: The secret sauce to <span className="font-bold text-[#2563EB]">our success.</span>
                                </h2>
                                <p className="text-gray-500 text-lg">
                                    What made the CaloriePal app project exceptional? Our relentless commitment to transparent communication that kept everyone aligned and excited throughout the development journey.
                                </p>
                            </div>
                            <div className="flex justify-start w-full md:w-auto">
                                <button className="border border-gray-300 rounded-full px-6 py-2 text-gray-700 bg-white hover:bg-gray-50 transition font-medium text-base">Project Success</button>
                            </div>
                        </div>

                        {/* Content Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                            {/* Left column - Communication Points */}
                            <div className="space-y-8">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#2563EB] flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 20h9"></path>
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Surprises, Just Progress</h3>
                                        <p className="text-gray-600">
                                            Daily updates via your preferred platform kept everyone informed, eliminating the anxiety of wondering "what's happening with my health app today?"
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#2563EB] flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Weekly Deep Dives That Matter</h3>
                                        <p className="text-gray-600">
                                            Our focused weekly meetings weren't just check-ins—they were collaborative strategy sessions that kept your health app vision at the center while driving momentum.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#2563EB] flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Problems Solved Before They Grew</h3>
                                        <p className="text-gray-600">
                                            Our team's proactive approach to React Native challenges meant issues were identified and resolved early—keeping the project on track and your stress levels low.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#2563EB] flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="3" y1="9" x2="21" y2="9"></line>
                                            <line x1="9" y1="21" x2="9" y2="9"></line>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Full Transparency, Zero Guesswork</h3>
                                        <p className="text-gray-600">
                                            Our project tracking system gave you real-time visibility into every mobile development milestone, eliminating the black-box development experience that plagues most projects.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Right side - Trello Card & Status Updates */}
                            <div className="relative space-y-2">
                                <div className="scale-75 mt-[-30px] overflow-hidden">
                                    <Image
                                        src={TrelloCard.src}
                                        alt="Daily Status Updates Dashboard"
                                        width={300}
                                        height={100}
                                        className="w-auto h-auto object-cover"
                                    />
                                </div>
                                {/* Daily Status Updates Image */}
                                <div className="rounded-2xl scale-90 mt-4 overflow-hidden">
                                    <Image
                                        src={DailyStatusUpdates.src}
                                        alt="Daily Status Updates Dashboard"
                                        width={600}
                                        height={400}
                                        className="w-full h-auto object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Banner Section */}
                <BannerSection
                    heading="Ready to build your AI mobile app?"
                    sub_heading="We built CaloriePal's Vision AI food recognition in 12 weeks. Let's scope your project."
                    button_text="Book free consultation →"
                    button_link="/contact-us"
                />
            </div>
        </>
    );
} 